/**
 * @fileoverview Composant de modale personnalisable avec en-tête, contenu et actions.
 * @module NewModal
 */

import { Button, CircularProgress } from '@material-ui/core';
import { useStyles } from '@webapps/styles';
import React from 'react';

const PADDING_HEADER = 24;
const PADDING_CONTENT = 24;

/**
 * @typedef {Object} ImageConfig
 * @property {string} src - URL de l'image
 * @property {string} [srcSet] - URL de l'image pour différentes tailles d'écran
 * @property {string} [sizes] - Tailles de l'image
 * @property {string} [alt] - Texte alternatif de l'image
 * @property {string} [width] - Largeur de l'image
 * @property {string} [height] - Hauteur de l'image
 */

/**
 * @typedef {Object} Action
 * @property {string} label - Texte du bouton
 * @property {Function} onClick - Fonction appelée au clic
 * @property {boolean} [disabled] - Désactive le bouton
 * @property {boolean} [loading] - Affiche un état de chargement
 * @property {'validateButton'|'deleteButton'} [type] - Type de style du bouton pour le faire correspondre avec une classe
 */

/**
 * Composant de modale personnalisable
 * @param {Object} props - Les propriétés du composant
 * @param {boolean} props.open - Contrôle l'affichage de la modale
 * @param {Function} props.onCancel - Fonction appelée lors de la fermeture
 * @param {React.ReactNode} [props.children] - Contenu principal de la modale
 * @param {ImageConfig} [props.image] - Configuration de l'image dans l'en-tête
 * @param {string} props.title - Titre de la modale
 * @param {string} props.subtitle - Sous-titre de la modale
 * @param {Action[]} [props.actions] - Liste des boutons d'action
 * @returns {React.ReactNode} Le composant de modale
 * 
 */
export default function NewModal({ open, onCancel, children, image, title, subtitle, actions, width, className }) {
  const { classes, cx } = useStyles(styles);
  if (!open) return null;

  // Empêche la propagation du clic sur le contenu de la modal
  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className={classes.overlay} onClick={onCancel}>
      <div className={cx(classes.modal, className)} onClick={stopPropagation} style={{ width: width }}>
        <div className={classes.close} onClick={onCancel} title="Fermer">
          &#10005;
        </div>
        <div className={classes.header}>
          {image && <img
            className={classes.logo}
            src={image?.src}
            srcSet={image?.srcSet}
            sizes={image?.sizes}
            width={image?.width}
            height={image?.height}
            alt={image?.alt}
          />}
          <div className={classes.headerText}>
            <div className={classes.title}>{title}</div>
            <div className={classes.description}>
              {subtitle}
            </div>
          </div>
        </div>
        {children && <div className={classes.content}>
          {children}
        </div>}
        {actions && <div className={classes.actions}>
          {actions.map((action, index) => (
            <Button key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              loading={action.loading}
              className={cx(classes.button, classes[action.type])}
              style={{
                width: `${100 / actions.length}%`
              }}
            >
              {action.loading ? <CircularProgress size={20} color='white' /> : action.label}
            </Button>
          ))}
        </div>}
      </div>
    </div >
  );
}

const styles = ({ spacing }) => ({
  overlay: {
    position: 'fixed',
    zIndex: 1000,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modal: {
    position: 'relative',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
    // padding: '32px 24px',
    zIndex: 2010,
  },
  content: {
    padding: `${PADDING_CONTENT}px`,
    paddingBottom: "0px",
  },
  close: {
    position: 'absolute',
    top: '-18px',
    right: '-18px',
    width: '36px',
    height: '36px',
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 22,
    color: '#888',
    zIndex: 2020,
    transition: 'background 0.2s'
  },
  header: {
    alignItems: "center",
    backgroundColor: "#f3f7ff",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    display: "flex",
    flexDirection: "column",
    padding: `${PADDING_HEADER}px`,
    width: "100%",
    gap: "30px",
    overflow: "visible",
  },
  logo: {
    // aspectRatio: "2.02054 / 1",
    // width: "166px",
    position: "relative",
    overflow: "visible",
  },
  headerText: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    width: "388px",
    gap: "10px",
    // overflow: "hidden",
  },
  title: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "24px",
    fontWeight: "600",
    lineHeight: "28px",
    textAlign: "center",
    color: "#152652",
    width: "100%",
  },
  description: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: "14px",
    lineHeight: "18px",
    textAlign: "center",
    color: "#5d75a8",
    width: "100%",
  },
  actions: {
    marginLeft: PADDING_CONTENT,
    marginRight: PADDING_CONTENT,
    marginTop: "20px", // car 20px de gap dans les modales
    marginBottom: PADDING_CONTENT,
    gap: "16px",
    display: "flex",
  },
  button: {
    // backgroundColor: "#0060f9",
    borderRadius: "40px",
    display: "flex",
    height: "40px",
    padding: "5px 15px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    overflow: "visible",
    // "&:hover": {
    //   backgroundColor: "#004ac1",
    // },
  },
  validateButton: {
    backgroundColor: "#0060f9",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#004ac1",
    },
  },
  deleteButton: {
    backgroundColor: "#F50057",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#BD0042",
    },
  },
  cancelButton: {
    backgroundColor: "#F3F7FF",
    color: "#5A6DA1",
    border: "1px solid #C0D9FF",
    "&:hover": {
      backgroundColor: "#0000000a",
    },
  },
});
